import { useAuth } from 'react-oidc-context';

import { parseStageFromUrl, Stage } from '../constants/serviceConstants';
import { getFromLocalStorage } from '../utils/localStorage';
import { ART_EVENT_ID_LOCAL_STORAGE_KEY } from '../constants/artEvent/artEventConstants';
import { API_BASE_URLS } from '../constants/url';

/**
 * Get the appropriate API base URL based on the current environment
 */
const getApiBaseUrl = (): string => {
  const url = new URL(window.location.href);
  const stage = parseStageFromUrl(url);

  switch (stage) {
    case Stage.BETA:
      return API_BASE_URLS[Stage.BETA];
    case Stage.PROD:
      return API_BASE_URLS[Stage.PROD];
    default:
      return API_BASE_URLS[Stage.DEV];
  }
};

/**
 * Build the full API URL for a given endpoint
 */
const buildApiUrl = (endpoint: string): string => {
  // Ensure endpoint starts with /
  const normalizedEndpoint = endpoint.startsWith('/')
    ? endpoint
    : `/${endpoint}`;

  const baseUrl = getApiBaseUrl();
  return `${baseUrl}${normalizedEndpoint}`;
};

// collects all abort controllers for each request tracked by url
const ACTIVE_API_CONTROLLER_MAP: Record<string, AbortController> = {};

/**
 * Custom hook for making authenticated API calls
 */
export const useApiClient = () => {
  const auth = useAuth();

  // Redirect to sign-in if not authenticated
  if (!auth.isAuthenticated) {
    auth.signinRedirect();
    throw new Error('Redirecting to sign-in');
  }

  const makeApiCall = async <T = any>(
    endpoint: string,
    options: RequestInit = {},
    retainPreviousRequest = false,
    responseType: 'json' | 'blob' = 'json'
  ): Promise<T | null> => {
    const url = buildApiUrl(endpoint);

    if (!retainPreviousRequest && ACTIVE_API_CONTROLLER_MAP[url]) {
      const apiController = ACTIVE_API_CONTROLLER_MAP[url];
      apiController.abort(`Request superseded by new request - URL is ${url}`);
      delete ACTIVE_API_CONTROLLER_MAP[url];
    }
    const controller = new AbortController();
    const signal = controller.signal;
    ACTIVE_API_CONTROLLER_MAP[url] = controller;

    const defaultHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${auth.user?.id_token}`,
    };

    const artEventId = getFromLocalStorage(ART_EVENT_ID_LOCAL_STORAGE_KEY);
    if (artEventId) {
      defaultHeaders['X-Event-Id'] = artEventId;
    }

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
      signal,
    };

    const response = await fetch(url, config);
    try {
      // This is required for response with no response body.
      // The Content-Length is always returned in the HTTP response even when there is no content, in which case the value is zero.
      const contentLength = response.headers.get('content-length');
      if (contentLength === '0') {
        if (response.ok) {
          return null;
        }
      }

      let data;
      if (responseType === 'blob') data = await response.blob();
      else data = await response.json();

      if (response.ok) {
        // True if status is HTTP 2xx
        return data;
      }

      if (responseType === 'blob') {
        // For blob responses, convert error to text
        const errorText = await data.text();
        return Promise.reject({
          message: errorText,
          statusCode: response.status,
        });
      }

      return Promise.reject({ ...data, statusCode: response.status });
    } catch (error) {
      if (error instanceof Error && error.name == 'AbortError') {
        // handle abort()
        error.message = `Active request to${url}was cancelled`;
      }
      return Promise.reject(error);
    }
  };

  const get = <T = any>(
    endpoint: string,
    responseType: 'json' | 'blob' = 'json'
  ): Promise<T | null> => {
    return makeApiCall<T>(endpoint, { method: 'GET' }, false, responseType);
  };

  const post = <T = any>(endpoint: string, data?: any): Promise<T | null> => {
    const isInstanceOfFormData = data instanceof FormData;

    const apiOptions: any = {
      method: 'POST',
      body: data
        ? isInstanceOfFormData
          ? data
          : JSON.stringify(data)
        : undefined,
    };

    if (isInstanceOfFormData) {
      apiOptions.headers = {
        'Content-Type': 'multipart/form-data',
      };
    }

    return makeApiCall<T>(endpoint, apiOptions);
  };

  const put = <T = any>(endpoint: string, data?: any): Promise<T | null> => {
    return makeApiCall<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  };

  const del = <T = any>(endpoint: string): Promise<T | null> => {
    return makeApiCall<T>(endpoint, { method: 'DELETE' });
  };

  return {
    makeApiCall,
    get,
    post,
    put,
    delete: del,
  };
};

export default useApiClient;
